/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import CreditsAPI from '../../api/CreditsAPI';

export const CREDIT_PACK_CREDITS = [
  100, 200, 400, 800, 1000, 2000, 3000, 4000, 5000, 7500, 10000, 20000, 50000,
] as const;

export const DATABASE_CREDIT_PACK_CREDITS = [
  ...CREDIT_PACK_CREDITS,
  200000,
] as const;

type CreditPackType =
  | 'database'
  | 'product_database'
  | 'analyzers'
  | 'workflows';

const DEFAULT_ANALYSIS_CREDIT_PACK_PRICES: Record<number, number> = {
  100: 30,
  200: 50,
  400: 85,
  800: 150,
  1000: 190,
  2000: 300,
  3000: 390,
  4000: 500,
  5000: 600,
  7500: 725,
  10000: 830,
  20000: 1400,
  50000: 3000,
};

// Database — monthly recurring. BITE Pricing.xlsx, Credit Pricing tab, "Product Database" /
// "Entity Database" Monthly columns (identical to each other at every tier — the basis for
// merging the two legacy pools into one Database pack per the approved plan, §1).
const DEFAULT_DATABASE_CREDIT_PACK_PRICES: Record<number, number> = {
  100: 30,
  200: 50,
  400: 85,
  800: 150,
  1000: 190,
  2000: 300,
  3000: 390,
  4000: 500,
  5000: 600,
  7500: 725,
  10000: 830,
  20000: 960,
  50000: 1200,
  200000: 0,
};

const DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES: Record<number, number> = {
  100: 30,
  200: 50,
  400: 85,
  800: 150,
  1000: 190,
  2000: 300,
  3000: 390,
  4000: 500,
  5000: 600,
  7500: 725,
  10000: 830,
  20000: 960,
  50000: 1200,
};

// Analyzers (Bulk Tariff Analyzer) — one-time purchase. BITE Pricing.xlsx, Credit Pricing tab,
// "Bulk Tariff Analyzer" Total column — close to the Audit Tools ladder but NOT identical
// (400/800 tiers are whole dollars: $85 / $150).
// Must equal the server's ANALYZERS_TIERS (creditPackCatalog.service.ts): that is what Stripe charges.
const DEFAULT_ANALYZERS_CREDIT_PACK_PRICES: Record<number, number> = {
  100: 30,
  200: 50,
  400: 85,
  800: 150,
  1000: 190,
  2000: 300,
  3000: 390,
  4000: 500,
  5000: 600,
  7500: 725,
  10000: 830,
  20000: 1400,
  50000: 3000,
};

// Workflows — monthly recurring, spent at a variable per-run cost (Import=4,
// Export=10, Design Assist=10, Beneficial Ownership=10, Export and
// Transshipment Risk=10 credits per completed run) rather than a flat
// per-credit rate; the server's WORKFLOW_CREDIT_COSTS is the source of truth
// for those. Tiers are keyed by "runs per month" (BITE Pricing.xlsx
// "Workflow" column), but the actual credit pack size is runs × 10 (the
// dearest run type — sizing the pack so it always covers that many runs of
// anything); the tier's total is that credit count × the row's
// price-per-credit.
const WORKFLOWS_RUNS_PER_MONTH = [
  5, 10, 20, 50, 75, 100, 150, 300, 500, 750, 1000,
] as const;

const WORKFLOWS_CREDIT_PACK_PRICE_PER_CREDIT: Record<number, number> = {
  5: 0.5,
  10: 0.46,
  20: 0.42,
  50: 0.4,
  75: 0.39,
  100: 0.38,
  150: 0.35,
  300: 0.3,
  500: 0.25,
  750: 0.2,
  1000: 0.15,
};

export const WORKFLOWS_CREDIT_PACK_CREDITS = WORKFLOWS_RUNS_PER_MONTH.map(
  (runs) => runs * 10,
);

const DEFAULT_WORKFLOWS_CREDIT_PACK_PRICES: Record<number, number> = Object.fromEntries(
  WORKFLOWS_RUNS_PER_MONTH.map((runs) => {
    const credits = runs * 10;
    return [
      credits,
      Math.round(credits * WORKFLOWS_CREDIT_PACK_PRICE_PER_CREDIT[runs] * 100) / 100,
    ];
  }),
);

// Legacy Entity DB / Product DB price IDs -- these two packs are retired from
// new purchases (see databaseCombinedCreditPackPriceIds below) and kept ONLY
// to classify teams still on the pre-merge legacy subscriptions, so they stay
// on the old build-time env vars: they're fixed historical price IDs that
// will never be recreated, unlike the four packs below.
const getPriceIdMapByPrefix = (
  prefix: string,
  creditsList: readonly number[] = CREDIT_PACK_CREDITS,
): Record<number, string> =>
  creditsList.reduce((acc, credits) => {
    acc[credits] = process.env[`${prefix}${credits}_PRICE`] || '';
    return acc;
  }, {} as Record<number, string>);

export const databaseCreditPackPriceIds = getPriceIdMapByPrefix(
  'REACT_APP_DATABASE_CREDITS_',
  DATABASE_CREDIT_PACK_CREDITS,
);

export const productDatabaseCreditPackPriceIds = getPriceIdMapByPrefix(
  'REACT_APP_PRODUCT_CREDITS_',
);

// Tools / Analyzers / Database (combined) / Workflows -- these four packs'
// price IDs now come from the server's live Stripe catalog
// (GET /api/v1/credits/packs, see server's creditPackCatalog.service.ts)
// instead of REACT_APP_*_PRICE env vars baked in at build time. Exported as
// the same mutable objects the rest of the app already imports; loadCredit
// PackCatalog mutates them in place once the fetch resolves, so no consumer
// needs to change how it reads them -- only components that render before
// the fetch resolves need useCreditPackCatalog() to know when to re-render.
export const analyzersCreditPackPriceIds: Record<number, string> = {};
export const databaseCombinedCreditPackPriceIds: Record<number, string> = {};
export const workflowsCreditPackPriceIds: Record<number, string> = {};

type CreditPackCatalogResponse = Record<
  'tools' | 'analyzers' | 'database' | 'workflows',
  Record<string, { priceId: string; amount: number }>
>;

const applyCatalogEntries = (
  target: Record<number, string>,
  entries: Record<string, { priceId: string; amount: number }> | undefined,
): void => {
  Object.entries(entries ?? {}).forEach(([credits, entry]) => {
    target[Number(credits)] = entry.priceId;
  });
};

let catalogLoadPromise: Promise<void> | null = null;
let catalogLoaded = false;
const catalogListeners = new Set<() => void>();

/**
 * Fetches the live Stripe credit-pack catalog from the server exactly once
 * (subsequent calls return the same in-flight/settled promise), and mutates
 * the four price-id maps above in place. Call from a component that renders
 * price IDs before user interaction (e.g. SubscriptionPricing.tsx) so the
 * fetch kicks off as early as possible; pair with useCreditPackCatalog() to
 * re-render once it resolves.
 */
export const loadCreditPackCatalog = (): Promise<void> => {
  if (!catalogLoadPromise) {
    catalogLoadPromise = CreditsAPI.getCreditPackCatalog()
      .then((resp: { success: boolean; result?: CreditPackCatalogResponse }) => {
        if (resp?.success && resp.result) {
          applyCatalogEntries(analyzersCreditPackPriceIds, resp.result.analyzers);
          applyCatalogEntries(databaseCombinedCreditPackPriceIds, resp.result.database);
          applyCatalogEntries(workflowsCreditPackPriceIds, resp.result.workflows);
        }
      })
      .catch(() => {
        // Leave the maps empty on failure -- callers already treat a missing
        // price ID as "this pack isn't purchasable right now" rather than
        // throwing, same as the old env-var-missing case.
      })
      .finally(() => {
        catalogLoaded = true;
        catalogListeners.forEach((listener) => listener());
      });
  }
  return catalogLoadPromise;
};

/** True once loadCreditPackCatalog() has settled (success or failure). */
export const isCreditPackCatalogLoaded = (): boolean => catalogLoaded;

/**
 * Triggers loadCreditPackCatalog() and re-renders the calling component once
 * it settles, so price-id maps that were empty on first render show real
 * values without a manual reload. Safe to call from multiple components --
 * they share the one underlying fetch.
 */
export const useCreditPackCatalog = (): boolean => {
  const [ready, setReady] = useState(catalogLoaded);

  useEffect(() => {
    if (catalogLoaded) {
      setReady(true);
      return undefined;
    }

    const listener = () => setReady(true);
    catalogListeners.add(listener);
    loadCreditPackCatalog();

    return () => {
      catalogListeners.delete(listener);
    };
  }, []);

  return ready;
};

export const getCreditPackPriceIdMap = (
  type: CreditPackType,
): Record<number, string> => {
  switch (type) {
    case 'database':
      return databaseCombinedCreditPackPriceIds;
    case 'analyzers':
      return analyzersCreditPackPriceIds;
    case 'workflows':
      return workflowsCreditPackPriceIds;
    case 'product_database':
    default:
      return productDatabaseCreditPackPriceIds;
  }
};

export const analysisCreditPacks = [
  { credits: 100, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[100] },
  { credits: 200, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[200] },
  { credits: 400, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[400] },
  { credits: 800, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[800] },
  { credits: 1000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[1000] },
  { credits: 2000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[2000] },
  { credits: 3000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[3000] },
  { credits: 4000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[4000] },
  { credits: 5000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[5000] },
  { credits: 7500, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[7500] },
  { credits: 10000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[10000] },
  { credits: 20000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[20000] },
  { credits: 50000, price: DEFAULT_ANALYSIS_CREDIT_PACK_PRICES[50000] },
];

export const databaseCreditPacks = [
  { credits: 100, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[100] },
  { credits: 200, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[200] },
  { credits: 400, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[400] },
  { credits: 800, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[800] },
  { credits: 1000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[1000] },
  { credits: 2000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[2000] },
  { credits: 3000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[3000] },
  { credits: 4000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[4000] },
  { credits: 5000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[5000] },
  { credits: 7500, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[7500] },
  { credits: 10000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[10000] },
  { credits: 20000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[20000] },
  { credits: 50000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[50000] },
  { credits: 200000, price: DEFAULT_DATABASE_CREDIT_PACK_PRICES[200000] },
];

export const productDatabaseCreditPacks = [
  { credits: 100, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[100] },
  { credits: 200, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[200] },
  { credits: 400, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[400] },
  { credits: 800, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[800] },
  { credits: 1000, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[1000] },
  { credits: 2000, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[2000] },
  { credits: 3000, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[3000] },
  { credits: 4000, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[4000] },
  { credits: 5000, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[5000] },
  { credits: 7500, price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[7500] },
  {
    credits: 10000,
    price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[10000],
  },
  {
    credits: 20000,
    price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[20000],
  },
  {
    credits: 50000,
    price: DEFAULT_PRODUCT_DATABASE_CREDIT_PACK_PRICES[50000],
  },
];

export const analyzersCreditPacks = CREDIT_PACK_CREDITS.map((credits) => ({
  credits: credits,
  price: DEFAULT_ANALYZERS_CREDIT_PACK_PRICES[credits],
}));

export const workflowsCreditPacks = WORKFLOWS_CREDIT_PACK_CREDITS.map((credits) => ({
  credits: credits,
  price: DEFAULT_WORKFLOWS_CREDIT_PACK_PRICES[credits],
}));

export const creditPacks = analysisCreditPacks;

// Pack prices are rendered with two decimals (e.g. "$85.00") so a price never
// displays as "$85.2" -- use this wherever a pack price is rendered.
export const formatCreditPackPrice = (amount: number): string =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export type CreditSelections = Record<string, number | null>;

export const initialCreditSelections: CreditSelections = {
  'trade-compliance-bulk': null,
  'ai-agent': null,
  'erp-integration': null,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getCreditCalcTotals(selections: CreditSelections) {
  let totalPrice = 0;
  let totalCredits = 0;
  for (const id of Object.keys(selections)) {
    const credits = selections[id];
    if (credits === null) continue;
    const pack = creditPacks.find((p) => p.credits === credits);
    if (pack) {
      totalPrice += pack.price;
      totalCredits += pack.credits;
    }
  }
  return { totalPrice: totalPrice, totalCredits: totalCredits };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getRequiredCreditPack(totalCredits: number) {
  if (totalCredits <= 0) return null;
  return (
    creditPacks.find((p) => p.credits >= totalCredits) ||
    creditPacks[creditPacks.length - 1]
  );
}

type RowDef = {
  id: string;
  label: string;
  features: string[];
  unlimited: boolean;
};

const rows: RowDef[] = [
  {
    id: 'hts-lookup',
    label: 'Tariff Look-up Tool: Single HTS Look-up',
    unlimited: true,
    features: ['HTS code generation', 'Tariff simulation', 'Duty rate lookup'],
  },
  {
    id: 'trade-compliance-single',
    label: 'Trade Compliance Tools: Single Operation',
    unlimited: true,
    features: [
      'Import risk screening',
      'Export control checks',
      'Denied party screening',
    ],
  },
  {
    id: 'trade-compliance-bulk',
    label: 'ACE and Tariff Analyzers: Bulk Operations',
    unlimited: false,
    features: ['Batch denied party screening', 'Multi-item compliance checks'],
  },
  {
    id: 'ai-agent',
    label: 'AI Agent Automation',
    unlimited: false,
    features: [
      'Agentic import compliance',
      'Agentic export control',
      'Automated decision workflows',
    ],
  },
  {
    id: 'erp-integration',
    label: 'BITE Database: ERP Integration',
    unlimited: false,
    features: [
      'Real-time data sync',
      'Automated record updates',
      'Custom field mapping',
    ],
  },
];

import {
  IconCalculator,
  IconX,
  IconZap,
  IconInfinity,
  IconShoppingCart,
} from './SVGIcons';

interface CreditCalculatorProps {
  selections: CreditSelections;
  onSelectionsChange: (selections: CreditSelections) => void;
  onClose?: () => void;
}

const CreditCalculator: React.FC<CreditCalculatorProps> = ({
  selections,
  onSelectionsChange,
  onClose,
}: CreditCalculatorProps) => {
  const getSelectedPack = (id: string) => {
    const credits = selections[id];
    if (credits === null) return null;
    return creditPacks.find((p) => p.credits === credits) || null;
  };
  const { totalCredits } = getCreditCalcTotals(selections);

  return (
    <div>
      <div className='cc-header'>
        <div className='cc-header-icon'>
          <IconCalculator />
        </div>
        <div className='cc-header-text'>
          <div className='cc-title'>Credit Calculator</div>
          <div className='cc-subtitle'>
            Estimate your credit needs and see pricing per feature
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className='icon-btn-round'>
            <IconX />
          </button>
        )}
      </div>

      <table className='calc-table'>
        <thead>
          <tr>
            <th>License Description</th>
            <th>Credits Needed</th>
            <th className='cc-th-right'>Price per Credit</th>
            <th className='cc-th-right'>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const pack = row.unlimited ? null : getSelectedPack(row.id);
            const ppc = pack ? (pack.price / pack.credits).toFixed(2) : null;
            return (
              <tr key={row.id}>
                <td className='cc-row-label'>
                  <div>{row.label}</div>
                  <div className='cc-feature-list'>
                    {row.features.map((f, i) => (
                      <div key={i} className='cc-feature-item'>
                        • {f}
                      </div>
                    ))}
                  </div>
                </td>
                <td>
                  {row.unlimited ? (
                    <span className='cc-unlimited'>
                      <IconInfinity /> Unlimited
                    </span>
                  ) : (
                    <select
                      className='cc-credit-select'
                      value={
                        selections[row.id] !== null
                          ? String(selections[row.id])
                          : ''
                      }
                      onChange={(e) =>
                        onSelectionsChange({
                          ...selections,
                          [row.id]: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    >
                      <option value=''>Select pack</option>
                      {creditPacks.map((p) => (
                        <option key={p.credits} value={String(p.credits)}>
                          {p.credits.toLocaleString()} credits — ${formatCreditPackPrice(p.price)}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className='cc-td-right'>
                  {row.unlimited ? (
                    <span className='cc-dash'>—</span>
                  ) : pack ? (
                    <span className='cc-ppc-value'>${ppc}</span>
                  ) : (
                    <span className='cc-dash'>—</span>
                  )}
                </td>
                <td className='cc-td-right'>
                  {row.unlimited ? (
                    <span className='cc-included'>Included</span>
                  ) : pack ? (
                    <span className='cc-subtotal-value'>
                      ${formatCreditPackPrice(pack.price)}
                    </span>
                  ) : (
                    <span className='cc-dash'>—</span>
                  )}
                </td>
              </tr>
            );
          })}
          {totalCredits > 0 && (
            <>
              <tr className='totals-row'>
                <td className='cc-totals-label'>Total Estimated Credits</td>
                <td>
                  <div className='cc-total-credits'>
                    <IconZap /> {totalCredits.toLocaleString()} credits
                  </div>
                </td>
                <td />
                <td />
              </tr>
              {(() => {
                const requiredPack = getRequiredCreditPack(totalCredits);
                if (!requiredPack) return null;
                return (
                  <tr className='pack-row'>
                    <td className='cc-pack-label'>
                      <div className='cc-pack-label-inner'>
                        <IconShoppingCart /> Credit Pack Required
                      </div>
                    </td>
                    <td className='cc-pack-credits'>
                      {requiredPack.credits.toLocaleString()} credits
                    </td>
                    <td className='cc-pack-ppc'>
                      ${(requiredPack.price / requiredPack.credits).toFixed(2)}
                    </td>
                    <td className='cc-pack-total'>
                      ${formatCreditPackPrice(requiredPack.price)}
                    </td>
                  </tr>
                );
              })()}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CreditCalculator;
